import type { ChapterCourseDifficultyWithAll, LimitOffsetSort, SortQuery } from '@jojotique/server'
import { env } from '@utils/env.ts'

interface GetUrlOptions {
  isAdmin?: boolean
  isComponents?: boolean
}

export const getUrlWithPagination = (
  url: string,
  { limit, page, sort, difficulty }: LimitOffsetSort,
  { isAdmin, isComponents }: GetUrlOptions,
): string => {
  const urlObj = new URL(url, import.meta.env.VITE_BACK)

  if (isAdmin) {
    urlObj.searchParams.set('all', 'true')
    return urlObj.toString()
  }

  if (isComponents) urlObj.searchParams.set('isComponent', 'true')

  if (limit) urlObj.searchParams.set('limit', limit)
  if (page) urlObj.searchParams.set('page', page ?? '1')
  if (sort) urlObj.searchParams.set('sort', sort)
  if (difficulty) urlObj.searchParams.set('difficulty', difficulty)

  return urlObj.toString()
}

export const checkSortValue = (value?: string | null): value is SortQuery => {
  return ['newest', 'oldest', 'title_asc', 'title_desc'].includes(value ?? '')
}

export const checkDifficultyValue = (value?: string | null): value is ChapterCourseDifficultyWithAll => {
  return ['baby', 'medium', 'hard', 'hell'].includes(value ?? '')
}

export const addQueryParam = (key: string, value: string): void => {
  const url = new URL(window.location.href)
  url.searchParams.set(key, value)
  window.history.pushState({}, '', url.toString())
}

export const deleteQueryParam = (key: string): void => {
  const url = new URL(window.location.href)
  url.searchParams.delete(key)
  window.history.pushState({}, '', url.toString())
}

export const hasQueryParam = (key: string): boolean => {
  const url = new URL(window.location.href)
  return url.searchParams.has(key)
}

export const getUrlParams = (url: string | RegExp): string[] => {
  const match = window.location.pathname.match(url)
  if (!match) return []
  return match.slice(1).map((param) => decodeURIComponent(param))
}

export const updatePageTitle = (title: string): void => {
  document.title = title
  const titleElement = document.querySelector('title')
  if (titleElement) titleElement.textContent = `${title} | ${import.meta.env.VITE_SITE_NAME}`
}

export const getImgUrl = (imgName: string, isProd = false): string => `${env('VITE_BACK_IMG_URL', isProd)}/${imgName}`

export const getVideoIframe = (document: Document, videoUrl: string): HTMLIFrameElement => {
  const iframe: HTMLIFrameElement = document.createElement('iframe')
  iframe.src = videoUrl
  iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
  iframe.referrerPolicy = 'strict-origin-when-cross-origin'
  iframe.allowFullscreen = true
  iframe.classList.add('neon-border-primary')
  return iframe
}
